import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import RichText from "../components/RichText";
import Footnotes from "../components/Footnotes";
import MetadataModal from "../components/MetadataModal";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { getIssueTopic } from "../api/issuesApi";
import { API_ORIGIN } from "../api/authApi";
import { localizeTitle } from "../utils/textDirection";

// Section A: public. One page per card (/issues/:id/topics/:topicId),
// opened by clicking a card on the issue's detail page. Mirrors
// TopicDetailPage.js.
//
// Bilingual by design: title_fa/title_en etc. live together on the same
// row (see domain.IssueTopic on the backend) — activeLang just picks
// which half of that single page is currently shown, with the
// LanguageSwitcher toggling it in place rather than navigating anywhere.
function IssueTopicDetailPage() {
  const { id, topicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeLang, setActiveLang] = useState("fa");

  useEffect(() => {
    getIssueTopic(topicId)
      .then((t) => {
        setTopic(t);
        setActiveLang(t.title_fa ? "fa" : "en");
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [topicId]);

  if (loading) {
    return (
      <div className="site">
        <SiteHeader />
        <main className="pub-not-found">
          <p>در حال بارگذاری...</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (notFound || !topic) {
    return (
      <div className="site">
        <SiteHeader />
        <main className="pub-not-found">
          <p>این کارت یافت نشد.</p>
          <Link to={`/issues/${id}`} className="back-link">
            ← بازگشت به شماره
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const hasFa = Boolean(topic.title_fa);
  const hasEn = Boolean(topic.title_en);
  const title = (activeLang === "en" ? topic.title_en : topic.title_fa) || topic.title_fa || topic.title_en;
  const body = (activeLang === "en" ? topic.body_en : topic.body_fa) || topic.body_fa || topic.body_en;
  const byline = (activeLang === "en" ? topic.byline_en : topic.byline_fa) || topic.byline_fa || topic.byline_en;
  const footnotes = activeLang === "en" ? topic.footnotes_en : topic.footnotes_fa;

  const { dir: titleDir, text: titleText } = localizeTitle(title);
  const { dir: bylineDir, text: bylineText } = localizeTitle(byline);
  const pageDir = activeLang === "en" ? "ltr" : "rtl";

  return (
    <div className="site">
      <SiteHeader />

      <main className="topic-detail" dir={pageDir} lang={activeLang}>
        <span className="corner corner-tr" />
        <span className="corner corner-tl" />
        <span className="corner corner-br" />
        <span className="corner corner-bl" />

        <Link to={`/issues/${id}`} className="back-link">
          ← بازگشت به شماره
        </Link>

        {topic.image_path && (
          <div className="topic-detail-image-frame">
            <img className="topic-detail-image" src={`${API_ORIGIN}${topic.image_path}`} alt={title} />
          </div>
        )}

        <header className="topic-detail-header">
          <div className="topic-detail-kicker">
            <span className="topic-detail-kicker-seg" />
            <span className="topic-detail-kicker-label">{topic.category}</span>
            <span className="topic-detail-kicker-seg" />
          </div>

          <h1 className="topic-detail-title" dir={titleDir}>{titleText}</h1>

          {byline && (
            <div className="topic-detail-rule">
              <span className="topic-detail-rule-dots" aria-hidden="true">
                ❖❖
              </span>
              <span className="topic-detail-rule-line" />
              <span className="topic-detail-byline" dir={bylineDir}>{bylineText}</span>
            </div>
          )}
        </header>

        <div className="content-metadata-row">
          <MetadataModal metadata={topic.metadata} />
          <LanguageSwitcher activeLang={activeLang} onChange={setActiveLang} hasFa={hasFa} hasEn={hasEn} />
        </div>

        {body ? (
          <RichText className="topic-detail-body" html={body} />
        ) : (
          <p className="topic-detail-body topic-detail-body--empty">
            هنوز متنی برای این کارت ثبت نشده است.
          </p>
        )}

        <Footnotes footnotes={footnotes} />

        <div className="topic-detail-end-mark" aria-hidden="true">
          ❖
        </div>
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}

export default IssueTopicDetailPage;
